import React, { useEffect } from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface ERPvsAIERPProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const ERPvsAIERP: React.FC<ERPvsAIERPProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  useEffect(() => {
    document.title = 'Traditional School ERP vs AI-Powered ERP – Which Is Better? | My Learning Board';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Compare traditional school ERP software with AI-powered ERP systems. See why AI ERP saves 60% admin time, predicts outcomes, and automates workflows. Full feature-by-feature comparison.');
    }
    return () => { document.title = 'My Learning Board – AI-Powered School Management Software & ERP in India'; };
  }, []);

  const comparisonRows = [
    { category: 'Data Entry', erp: 'Manual input for every record', aiErp: 'Auto-capture from Excel, images & forms', winner: 'ai' },
    { category: 'Attendance', erp: 'Mark present/absent manually or biometric', aiErp: 'Auto-tracking + pattern detection + absence prediction', winner: 'ai' },
    { category: 'Fee Management', erp: 'Generate bills, send reminders manually', aiErp: 'AI predicts defaults, auto-sends personalized reminders', winner: 'ai' },
    { category: 'Timetable', erp: 'Days of manual scheduling', aiErp: 'AI generates optimized schedule in seconds', winner: 'ai' },
    { category: 'Report Cards', erp: 'Subject-wise data entry, manual totals', aiErp: 'Auto-consolidation with best-of-N & weighted scores', winner: 'ai' },
    { category: 'Parent Communication', erp: 'Bulk SMS / email blasts', aiErp: 'Personalized AI messages in parent\'s language via WhatsApp', winner: 'ai' },
    { category: 'Analytics', erp: 'Basic reports & exports', aiErp: 'Predictive dashboards with actionable insights', winner: 'ai' },
    { category: 'Student Insights', erp: 'End-of-term report only', aiErp: 'Real-time at-risk detection & performance forecasting', winner: 'ai' },
    { category: 'Support', erp: 'Ticketing system, business hours', aiErp: '24/7 AI chatbot + human escalation', winner: 'ai' },
    { category: 'Setup Time', erp: '2-4 weeks training', aiErp: 'Self-learning AI, productive in days', winner: 'ai' },
  ];

  const erpLimitations = [
    { icon: 'bi-hourglass-split', title: 'Time-Consuming', desc: 'Every task requires manual data entry. Staff spend 60% of time on repetitive admin work.' },
    { icon: 'bi-eye-slash', title: 'Reactive, Not Proactive', desc: 'Traditional ERP shows you what happened. It can\'t predict what will happen next.' },
    { icon: 'bi-translate', title: 'One-Size-Fits-All', desc: 'Generic reports and communication. No personalization for individual students or parents.' },
    { icon: 'bi-arrow-repeat', title: 'Manual Workflows', desc: 'Every process needs human intervention — reminders, follow-ups, escalations, reports.' },
  ];

  const aiAdvantages = [
    { icon: 'bi-lightning-charge', title: 'Automation First', desc: 'AI handles repetitive tasks — attendance alerts, fee reminders, report generation — without human effort.' },
    { icon: 'bi-eye', title: 'Predictive Intelligence', desc: 'Know which students will struggle, which parents will default on fees — before it happens.' },
    { icon: 'bi-person-heart', title: 'Personalized Experience', desc: 'Every parent gets communication in their language. Every student gets tailored insights.' },
    { icon: 'bi-speedometer2', title: '10x Faster Operations', desc: 'What took hours now takes seconds. Timetable? 30 seconds. Report cards? One click. Fee analysis? Instant.' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-8 mx-auto text-center text-white">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2">
                <i className="bi bi-arrow-left-right me-2"></i>Comparison Guide
              </span>
              <h1 className="display-4 fw-bold mb-4">
                Traditional School ERP <span style={{ color: '#FFC107' }}>vs</span> AI-Powered ERP
              </h1>
              <p className="lead mb-4 opacity-75">
                Your school ERP stores data. AI ERP thinks, predicts and acts on that data.
                See why 500+ schools have already switched.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <button className="btn btn-warning btn-lg px-4 text-dark" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>See AI ERP in Action
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Side by Side */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row g-4 mb-5">
            {/* ERP Limitations */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100" style={{ borderTop: '4px solid #DC3545' }}>
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 text-danger">
                    <i className="bi bi-x-circle me-2"></i>Traditional ERP Limitations
                  </h5>
                </div>
                <div className="card-body">
                  {erpLimitations.map((item, idx) => (
                    <div key={idx} className="d-flex mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: '44px', height: '44px', backgroundColor: '#DC354515' }}>
                        <i className={`bi ${item.icon}`} style={{ color: '#DC3545' }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1">{item.title}</h6>
                        <p className="text-muted small mb-0">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Advantages */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100" style={{ borderTop: '4px solid #28A745' }}>
                <div className="card-header bg-white py-3">
                  <h5 className="mb-0 text-success">
                    <i className="bi bi-check-circle me-2"></i>AI-Powered ERP Advantages
                  </h5>
                </div>
                <div className="card-body">
                  {aiAdvantages.map((item, idx) => (
                    <div key={idx} className="d-flex mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: '44px', height: '44px', backgroundColor: '#28A74515' }}>
                        <i className={`bi ${item.icon}`} style={{ color: '#28A745' }}></i>
                      </div>
                      <div>
                        <h6 className="mb-1">{item.title}</h6>
                        <p className="text-muted small mb-0">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Feature-by-Feature Comparison</h2>
            <p className="text-muted lead">A detailed look at what changes when AI enters the picture</p>
          </div>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ backgroundColor: '#1a1a2e', color: 'white' }}>
                      <th className="p-3">Category</th>
                      <th className="p-3">
                        <i className="bi bi-database me-2"></i>Traditional ERP
                      </th>
                      <th className="p-3">
                        <i className="bi bi-stars me-2" style={{ color: '#FFC107' }}></i>AI-Powered ERP
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map((row, idx) => (
                      <tr key={idx}>
                        <td className="p-3 fw-semibold">{row.category}</td>
                        <td className="p-3 text-muted">
                          <i className="bi bi-dash-circle me-2 text-danger"></i>{row.erp}
                        </td>
                        <td className="p-3">
                          <i className="bi bi-check-circle-fill me-2 text-success"></i>{row.aiErp}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">The ROI of Switching to AI ERP</h2>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              { number: '60%', label: 'Less Admin Time', icon: 'bi-clock-history', color: '#5C2597' },
              { number: '35%', label: 'Faster Fee Collection', icon: 'bi-cash-coin', color: '#28A745' },
              { number: '90%', label: 'Parent Satisfaction', icon: 'bi-emoji-smile', color: '#FFC107' },
              { number: '2x', label: 'Staff Productivity', icon: 'bi-lightning', color: '#2D7DD2' },
            ].map((stat, idx) => (
              <div key={idx} className="col-6 col-md-3">
                <div className="card border-0 shadow-sm text-center p-4">
                  <i className={`bi ${stat.icon} fs-2 mb-2`} style={{ color: stat.color }}></i>
                  <h2 className="fw-bold mb-1" style={{ color: stat.color }}>{stat.number}</h2>
                  <p className="text-muted small mb-0">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Ready to Upgrade from Traditional ERP?</h2>
          <p className="lead opacity-75 mb-4">See the AI difference with a free personalized demo</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
              <i className="bi bi-telephone me-2"></i>Book Free Demo
            </button>
            <button className="btn btn-outline-light btn-lg px-5" onClick={onNavigateToLogin}>
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ERPvsAIERP;
